// =================================================================
// useAdminAccess hook
//
// For a signed-in user, this hook determines which communities they
// can administer. Returns:
//   - loading: true while the lookup is in flight
//   - communities: array of community records (with codes) they admin
//   - isSuperAdmin: convenience pass-through from useAuth
//
// Super-admins technically admin all communities, but we don't load
// the full list here — they'll see them via the existing
// /admin/communities page which queries the full collection.
// For super-admins, `communities` returns empty (callers should
// special-case super-admin checks before reading this).
//
// Multi-admin model (Option A — reverse index):
//
//   A community can have more than one admin. Authorization is driven
//   by two sources, merged here:
//
//     1. admin_index/{emailKey}  — the reverse index. Maps a
//        sanitized email key to the set of community codes that email
//        administers. This is the primary, scalable source: any
//        co-admin added by the super-admin appears here. We read it
//        directly (a single keyed read, no query), then load each
//        referenced community record.
//
//     2. Legacy contact_email query — kept for backward compatibility.
//        Communities created before the multi-admin change only have
//        a single `contact_email` and may not yet have an admin_index
//        entry. We still query communities by contact_email so those
//        keep working with zero migration.
//
//   Results from both sources are merged and de-duplicated by code.
//
// The email key transform mirrors the one used when writing the
// index and the per-community `admins` node: dots in the email are
// replaced with commas (the only character illegal in a Firebase
// key that appears in a normal email; '@' is allowed). Keep this in
// sync with emailToKey() in AdminCommunities.jsx and the database
// rules (which use auth.token.email.replace('.', ',')).
// =================================================================

import { useEffect, useState } from 'react';
import { ref, query, orderByChild, equalTo, onValue, get } from 'firebase/database';
import { db } from './firebase.js';
import { useAuth } from './useAuth.jsx';

// Mirror of the rules' auth.token.email.replace('.', ',').
// RTDB rules replace ALL occurrences; String.prototype.replaceAll
// matches that. (A plain .replace with a string arg would only swap
// the first dot, which would NOT match the rules — use replaceAll.)
function emailToKey(email) {
  return String(email || '').toLowerCase().replaceAll('.', ',');
}

export function useAdminAccess() {
  const { user, isSuperAdmin, loading: authLoading } = useAuth();

  // Each source resolves independently; we merge once both are in.
  // null = not yet resolved, {} / [] = resolved-empty.
  const [indexCommunities, setIndexCommunities] = useState(null);
  const [legacyCommunities, setLegacyCommunities] = useState(null);
  const [error, setError] = useState(null);

  // ---- Source 1: reverse index (admin_index/{emailKey}) ----
  useEffect(() => {
    if (authLoading || !user) {
      setIndexCommunities(null);
      return;
    }
    if (isSuperAdmin) {
      setIndexCommunities([]);
      return;
    }

    let cancelled = false;
    const emailKey = emailToKey(user.email);
    const idxRef = ref(db, `brain_health/admin_index/${emailKey}`);

    const unsubscribe = onValue(
      idxRef,
      async (snap) => {
        const val = snap.val();
        if (!val) {
          if (!cancelled) setIndexCommunities([]);
          return;
        }
        // val is { [communityCode]: { email, name, added_at } }.
        // Load each referenced community record. We read them in
        // parallel; any that fail (deleted community, rules) are
        // simply skipped.
        const codes = Object.keys(val);
        const results = await Promise.all(
          codes.map(async (code) => {
            try {
              const cSnap = await get(
                ref(db, `brain_health/communities/${code}`)
              );
              const data = cSnap.val();
              return data ? { code, ...data } : null;
            } catch {
              return null;
            }
          })
        );
        if (!cancelled) {
          setIndexCommunities(results.filter(Boolean));
        }
      },
      (err) => {
        console.error('useAdminAccess admin_index read failed:', err);
        if (!cancelled) {
          setError(err.message);
          setIndexCommunities([]);
        }
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [authLoading, user, isSuperAdmin]);

  // ---- Source 2: legacy contact_email query ----
  useEffect(() => {
    if (authLoading || !user) {
      setLegacyCommunities(null);
      return;
    }
    if (isSuperAdmin) {
      setLegacyCommunities([]);
      return;
    }

    const q = query(
      ref(db, 'brain_health/communities'),
      orderByChild('contact_email'),
      equalTo(user.email)
    );

    const unsubscribe = onValue(
      q,
      (snap) => {
        const val = snap.val();
        if (!val) {
          setLegacyCommunities([]);
          return;
        }
        const list = Object.entries(val).map(([code, data]) => ({
          code,
          ...data,
        }));
        setLegacyCommunities(list);
      },
      (err) => {
        console.error('useAdminAccess contact_email query failed:', err);
        // Don't surface this as a hard error — the index is the
        // primary source. A rules mismatch here just means no legacy
        // matches.
        setLegacyCommunities([]);
      }
    );

    return unsubscribe;
  }, [authLoading, user, isSuperAdmin]);

  // Merge the two sources, de-duplicating by community code. Index
  // records win on conflict (they're loaded fresh per-path), but the
  // shapes are identical so it doesn't matter much.
  const bothResolved =
    indexCommunities !== null && legacyCommunities !== null;

  let communities = null;
  if (isSuperAdmin) {
    communities = [];
  } else if (bothResolved) {
    const byCode = new Map();
    for (const c of legacyCommunities) byCode.set(c.code, c);
    for (const c of indexCommunities) byCode.set(c.code, c);
    communities = Array.from(byCode.values()).sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    );
  }

  const loading = authLoading || (!!user && communities === null);

  return {
    loading,
    communities: communities || [],
    isSuperAdmin,
    error,
    hasAnyAccess:
      isSuperAdmin || (communities && communities.length > 0),
  };
}
